import { ErrorLike } from "app-types";

type LogType = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';
type LogCallback = (message: string, type: LogType) => void;

let logCallbacks: LogCallback[] = [];

const logMethodMap: Record<LogType, string> = {
  DEBUG: 'log',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
};

export const logMapping: Record<LogType, number> = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

/* eslint-disable no-console */
const consoleRawLog = window.consoleRawLog || console.log.bind(console);
const consoleRawInfo = window.consoleRawInfo || console.info.bind(console);
const consoleRawWarn = window.consoleRawWarn || console.warn.bind(console);
const consoleRawError = window.consoleRawError || console.error.bind(console);
/* eslint-disable */

let level = 1;
let enabled = true;

const handleLog = (message: string, type: LogType): void => {
  const logMethod = logMethodMap[type];

  let log: typeof console.log;
  switch (logMethod) {
    case 'info':
      log = consoleRawInfo;
      break;
    case 'warn':
      log = consoleRawWarn;
      break;
    case 'error':
      log = consoleRawError;
      break;
    default:
      log = consoleRawLog;
      break;
  }

  log(message);

  logCallbacks.forEach(callback => {
    callback(message, type);
  });
};

const describeError = (err: ErrorLike, showStack?: boolean): string | number | null | undefined => {

  let msg: string;
  if (err instanceof Error || err.stack) {
    msg = err.toString();
  } else if (err.message) {
    msg = err.message;
  } else if (err.type && err.target) {
    // Ignore JS events, they don't seem to be helpful.
  } else if (Object.prototype.toString !== err.toString) {
    msg = err.toString();
  } else {
    msg = JSON.stringify(err);
  }

  if (!msg && err.code) msg = `errCode: ${err.code}`;

  if (err.stack && showStack) {
    // The stack does not include the message on some platforms. Ensure it is present.
    if (err.message && err.stack.indexOf(err.message) >= 0) {
      // Message is already in the stack trace.
      msg = err.stack;
    } else {
      msg += `\n${err.stack}`;
    }
  }

  if (!msg) msg = 'unknown error';
  return msg;
};

const logger = {
  debug: (message: string): void => {
    handleLog(message, 'DEBUG');
  },
  info: (message: string): void => {
    handleLog(message, 'INFO');
  },
  warn: (message: string): void => {
    handleLog(message, 'WARN');
  },
  error: (message: string): void => {
    handleLog(message, 'ERROR');
  },
  addListener: (callback: LogCallback): void => {
    if (!callback || logCallbacks.find(item => item === callback)) {
      return;
    }

    logCallbacks = [...logCallbacks, callback];
  },
  removeListener: (callback: LogCallback): void => {
    if (!callback) {
      return;
    }

    logCallbacks = logCallbacks.filter(item => item !== callback);
  },
  removeAllListeners: (): void => {
    logCallbacks = [];
  },
  describeError,
  describeErrorWithStack: (err: unknown): string | number | null | undefined => describeError(err, true),
};

export default logger;

export const getLogLevelFromType = (type: string): number =>
  type in logMapping ? logMapping[type as LogType] : logMapping.INFO;

export const shouldLog = (type: string): boolean => enabled && getLogLevelFromType(type) >= level;

export const setLevel = (value: number): void => {
  level = value;
};

export const setEnable = (value: boolean): void => {
  enabled = value;
};

// Not calling this anymore because it caused infinite render loops
export const captureConsoleMessages = (): void => {
  /* eslint-disable no-console */
  console.log = (msg: string): void => {
    logger.debug(msg);
  };

  console.info = (msg: string): void => {
    logger.info(msg);
  };

  console.warn = (msg: string): void => {
    logger.warn(msg);
  };

  console.error = (msg: string): void => {
    logger.error(msg);
  };
  /* eslint-enable */
};
